import { useEffect, useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import { useToday } from '@/hooks/useToday';
import {
  activityFacts, effectiveStats, badgeProgress, settledWriteBack, levelProgress as levelProgressOf,
  type ActivityFacts, type ActivityHistory, type Badge, type BadgeProgress, type LevelProgress,
} from '@trackmind/core';

/** Badge plus the view-only "has the user got this yet" flag. */
export type BadgeWithStatus = Badge & { earned: boolean };

/**
 * XP, level, streaks and badges — core's engine (services/badges.ts) over the
 * stored record and the history. The header, Badges & XP and the assistant all
 * read these numbers the same way, so they can't disagree.
 *
 * Derived on each change to tasks or habit instances (a completion), not on
 * every render.
 */
/**
 * The last derivation, shared. Dashboard and the header's UserStats both call
 * this hook over the same context arrays; without the share each would walk
 * the whole history on every completion.
 */
let lastFacts: { history: ActivityHistory; facts: ActivityFacts } | null = null;
function sharedFacts(history: ActivityHistory): ActivityFacts {
  const last = lastFacts?.history;
  if (last && last.tasks === history.tasks && last.habitInstances === history.habitInstances
    && last.today === history.today && last.timezone === history.timezone) {
    return lastFacts!.facts;
  }
  const facts = activityFacts(history);
  lastFacts = { history, facts };
  return facts;
}

/** The settled record last written back, so two mounted callers write it once. */
let persistedBadges = '';

export function useGamification() {
  const { stats, loadStats, saveStats, isInitialized, tasks, habitInstances, preferences } = useApp();
  const todayKey = useToday();
  const zone = preferences?.timezone;

  const facts = useMemo(
    () => sharedFacts({ tasks, habitInstances, today: todayKey(), timezone: zone }),
    [tasks, habitInstances, todayKey, zone],
  );

  const settled = useMemo(() => (stats ? effectiveStats(stats, facts) : null), [stats, facts]);

  // A badge history earns but the record doesn't hold yet is written back —
  // only the badges, onto the stored record — so it stays earned even if the
  // history behind it is later deleted, and its earned date stops moving.
  // Waits for the initial load: a half-loaded history earns nothing, and the
  // defaults must never be written over a real record.
  //
  // The settled totals go back too (settledWriteBack in core): after a sync
  // the stored total holds the newer device's figure, and only the merged
  // history holds both devices' work.
  useEffect(() => {
    if (!isInitialized || !stats || !settled) return;
    const next = settledWriteBack(stats, settled);
    if (!next) return;
    const key = `${next.total_xp}|${next.tasks_completed}|${next.habits_completed}|${next.longest_streak}|${next.earned_badges.join(',')}`;
    if (persistedBadges === key) return;
    persistedBadges = key;
    void saveStats(next);
  }, [isInitialized, stats, settled, saveStats]);

  const levelProgress = useMemo(
    (): LevelProgress | null => (settled ? levelProgressOf(settled.total_xp) : null),
    [settled],
  );

  const progress = useMemo(
    (): BadgeProgress[] => (settled ? badgeProgress(settled, facts) : []),
    [settled, facts],
  );

  const badges = useMemo(
    (): BadgeWithStatus[] => progress.map((p) => ({ ...p.badge, earned: p.earned })),
    [progress],
  );
  const earnedBadges = useMemo(() => badges.filter((b) => b.earned), [badges]);
  const unearnedBadges = useMemo(() => badges.filter((b) => !b.earned), [badges]);

  return {
    stats: settled,
    facts,
    levelProgress,
    badgeProgress: progress,
    badges,
    earnedBadges,
    unearnedBadges,
    loading: false,
    error: null,
    refetch: loadStats,
  };
}
